import addFit from 'widget/utils/helpers/addFit';
import BigThumbnail from 'widget/components/BigThumbnail';
import END_REASON from 'modules/Analytics/constants/endReason';
import ExternalLink from 'widget/components/ExternalLink';
import InfoButton from 'widget/components/InfoButton';
import InfoCard from 'widget/components/InfoCard';
import LeftButtonGroup from 'widget/components/LeftButtonGroup';
import LeftButtonGroupControls from 'widget/components/LeftButtonGroupControls';
import mediaQueries from 'styles/mediaQueries';
import PlaybackError from 'widget/Errors/PlaybackError';
import PlayButton from 'widget/components/PlayButton';
import PlayButtonWrapper from 'widget/components/PlayButtonWrapper';
import player from 'widget/player';
import Player from 'widget/contexts/Player';
import PlayerContainer from 'widget/components/PlayerContainer';
import PlayerState, {
  PlayerErrorState,
} from 'modules/Player/constants/playerState';
import Progress from 'widget/components/Progress';
import RightButtonGroup from 'widget/components/RightButtonGroup';
import ShareButton from 'widget/components/ShareButton';
import ShareCard from 'widget/components/ShareCard';
import ShouldShow from 'components/ShouldShow';
import SkipBackButton from 'widget/components/SkipBackButton';
import SkipForwardButton from 'widget/components/SkipForwardButton';
import SpeedButton from 'widget/components/SpeedButton';
import Toolbar from 'widget/components/Toolbar';
import unescapeHTMLEntities from 'decode-html';
import useModal from 'widget/components/Modal/hooks/useModal';
import useMount from 'hooks/useMount';
import useTrackers from 'widget/contexts/Trackers/hooks/useTrackers';
import withPlayerJS from 'widget/components/withPlayerJS';
import { Events } from 'widget/trackers/types';
import {
  generatePodcastPageViewTrackingData,
  generatePodcastPlaybackTrackingData,
  getPodcastPlaybackAnalyticsPayload,
} from 'widget/Podcast/helpers/analytics';
import { PodcastProps } from 'widget/Podcast/types';
import { SyntheticEvent, useContext, useEffect, useRef, useState } from 'react';
import { usePlayerProgress } from 'widget/hooks';
import { withRetries } from 'widget/utils/helpers/withRetries';

function PodcastPlayer(props: PodcastProps) {
  const {
    analytics,
    analyticsData,
    autoplay,
    description,
    dimensions,
    disabled,
    episodeId,
    followUrl,
    getPlaybackErrorMessage,
    hasPlayed,
    imageUrl,
    isError,
    origQuery,
    podcastId,
    podcastUrl,
    positionBottom,
    refs,
    shareText,
    shareUrl,
    shareTitle,
    supportMobileView = false,
    title,
    urlWithQuery,
  } = props;
  const trackers = useTrackers();
  const {
    play: onClickPlay,
    playerState,
    playNextPodcast,
  } = useContext(Player.Context);
  const [playClicked, setPlayClicked] = useState(false);
  const [streamInitTime, setStreamInitTime] = useState<number>(0);
  const [isPlaybackError, setIsPlaybackError] = useState<boolean>(false);
  const trackingDataRef = useRef(
    generatePodcastPlaybackTrackingData({
      ...analyticsData,
      episodeId,
      podcastId,
    }),
  );
  const playedFromRef = useRef(analyticsData.playedFrom);
  const [progress] = usePlayerProgress();
  const isPlaying = playerState === PlayerState.Playing;
  const isBuffering = playerState === PlayerState.Buffering;
  const isLoading = playerState === PlayerState.Loading;

  const trackerData = {
    id: episodeId,
    name: analyticsData.subName,
    typeId: podcastId,
    type: 'podcast',
  };

  const [InfoModal, toggleInfoModal] = useModal(
    <InfoCard
      body={description ? unescapeHTMLEntities(description) : ''}
      title={title}
      url={shareUrl}
    />,
  );

  const [ShareModal, toggleShareModal] = useModal(
    <ShareCard
      analytics={analytics}
      analyticsData={{
        id: podcastId,
        name: analyticsData.name,
        subId: episodeId,
        subName: analyticsData.subName,
        type: 'podcast',
        subType: 'episode',
      }}
      dimensions={dimensions}
      followUrl={followUrl}
      includePositionToggle={!disabled}
      progress={progress}
      shareText={shareText}
      shareTitle={shareTitle}
      url={shareUrl}
      urlWithQuery={urlWithQuery}
    />,
  );

  const trackShare = () => {
    analytics?.trackPageView?.(
      generatePodcastPageViewTrackingData({
        name: analyticsData.name,
        pageName: 'share',
        podcastId,
      }),
    );

    toggleShareModal();
  };

  const play = (
    _e?: SyntheticEvent,
    playedFrom: number = analyticsData.playedFrom,
  ) => {
    if (playedFrom !== playedFromRef.current) {
      playedFromRef.current = playedFrom;
      trackingDataRef.current = generatePodcastPlaybackTrackingData({
        ...analyticsData,
        episodeId,
        playedFrom,
        podcastId,
      });
    }

    if (isPlaying) {
      trackers.track(Events.Pause);
      analytics?.track(Events.Pause, trackingDataRef.current);
      analytics?.track(
        Events.StreamEnd,
        getPodcastPlaybackAnalyticsPayload(trackingDataRef.current, {
          endReason: END_REASON.PAUSE,
          listenTime: Math.round(progress?.position ?? 0),
        }),
      );
    } else {
      analytics?.track(Events.Play, trackingDataRef.current);
      setStreamInitTime(Date.now());
      trackers.track(Events.Play, trackerData);
    }
    setPlayClicked(true);
    onClickPlay(podcastId, { streamUrlQuery: origQuery, trackId: episodeId });
  };

  const skipForward = () => {
    if (progress.duration) {
      player.seek(Math.min(progress.duration, progress.position + 30));
      if (analytics) {
        analytics.track(Events.Forward30, trackingDataRef.current);
      }
    }
  };

  const skipBack = () => {
    if (progress.duration) {
      player.seek(Math.max(progress.position - 15, 0));
      if (analytics) {
        analytics.track(Events.Back15, trackingDataRef.current);
      }
    }
  };

  const changeSpeed = async (newPlaybackSpeed: number) => {
    const oldPlaybackSpeed = await player.getPlaybackRate();
    player.setPlaybackRate(newPlaybackSpeed);
    if (analytics) {
      analytics.track(Events.SpeedChange, {
        ...trackingDataRef.current,
        speedChange: {
          newPlaybackSpeed,
          oldPlaybackSpeed,
        },
      });
    }
  };

  /**
   * IHRWEB-14940 methods mute, setCurrentTime, setLoop, setVolume, & unmute
   * are currently only externally triggered via player.js; react event
   * parameter included for methods with args for possible widget UI.
   * analytics tbd in IHRWEB-14917.
   */
  const mute = () => {
    player.mute();
    trackers.track(Events.Mute);
  };

  const setCurrentTime = (_e: null | SyntheticEvent, position: number) => {
    if (progress.duration) player.seek(position);
  };

  const setLoop = (_e: null | SyntheticEvent, shouldLoop: string | boolean) => {
    if (
      typeof shouldLoop === 'boolean' ||
      shouldLoop === 'true' ||
      shouldLoop === 'false'
    ) {
      player.setLoop(shouldLoop && shouldLoop !== 'false');
    }
  };

  const setVolume = (_e: null | SyntheticEvent, volume: string | number) => {
    player.setVolume(parseInt(volume as string, 10));
  };

  const unmute = () => {
    player.unmute();
    trackers.track(Events.Unmute, { isPlaying });
  };

  if (refs) {
    refs.methodTypes.current = {
      ...refs.methodTypes.current,
      isUnrestricted: true,
    };
    refs.mute.current = mute;
    refs.pause.current = play; // pause maps to toggling play method
    refs.play.current = play;
    refs.setCurrentTime.current = setCurrentTime;
    refs.setLoop.current = setLoop;
    refs.setVolume.current = setVolume;
    refs.unmute.current = unmute;
  }

  useMount(() => {
    if (autoplay) play();
  });

  const startedFromPosition = useRef<boolean>(false);
  useEffect(() => {
    if (origQuery) {
      const { position: _pos = 0 } = origQuery;
      if (
        !startedFromPosition.current &&
        Number(_pos) > 0 &&
        progress.duration
      ) {
        player.seek(Number(_pos));
        startedFromPosition.current = true;
      }
    }
  }, [progress, origQuery]);

  const playNextPodcastWithRetries = withRetries(
    () => playNextPodcast(podcastId, origQuery),
    () => setIsPlaybackError(true),
  );

  useEffect(() => {
    if (playerState === PlayerState.Complete) playNextPodcastWithRetries();
  }, [playerState]);

  useEffect(() => {
    if (playerState === PlayerState.Playing && streamInitTime) {
      analytics?.track(
        Events.StreamStart,
        getPodcastPlaybackAnalyticsPayload(trackingDataRef.current, {
          playbackStartTime: Date.now(),
          streamInitTime,
        }),
      );
      setStreamInitTime(0);
      trackers.track(Events.StreamStart, trackerData);
    }
  }, [playerState, streamInitTime]);

  useEffect(() => {
    if (isError) {
      const networkError = [
        PlayerErrorState.AccessDenied,
        PlayerErrorState.NetworkError,
      ].includes(playerState as PlayerErrorState);
      analytics?.track(
        Events.PlaybackError,
        getPodcastPlaybackAnalyticsPayload(trackingDataRef.current, {
          errorMessage: getPlaybackErrorMessage(
            playerState as PlayerErrorState,
          ),
          errorType: networkError ? 'network_error' : 'playback_failure',
        }),
      );
    }
  }, [isError]);

  if (isError && isPlaybackError) {
    return <PlaybackError error={playerState as PlayerErrorState} />;
  }

  return (
    <div>
      <PlayerContainer positionBottom={positionBottom}>
        <Progress
          hide={disabled}
          notYetInteractedWith={!isPlaying && !isBuffering && !isLoading}
          supportMobileView={supportMobileView}
        />
        <Toolbar>
          <LeftButtonGroup>
            <PlayButtonWrapper
              data-test="play-button-wrapper"
              notYetInteractedWith={!isPlaying && !isBuffering && !isLoading}
              supportMobileView={supportMobileView}
            >
              <PlayButton
                disabled={disabled}
                isLoading={isBuffering || isLoading}
                isPlaying={isPlaying || isBuffering || isLoading}
                onClick={play}
              />
            </PlayButtonWrapper>
            <ShouldShow shouldShow={hasPlayed || playClicked}>
              <LeftButtonGroupControls>
                <SkipBackButton
                  onClick={skipBack}
                  sizeToHide={mediaQueries.max.width['149']}
                />
                <SkipForwardButton
                  onClick={skipForward}
                  sizeToHide={mediaQueries.max.width['149']}
                />
                <SpeedButton
                  onClick={changeSpeed}
                  sizeToHide={mediaQueries.max.width['199']}
                />
              </LeftButtonGroupControls>
            </ShouldShow>
          </LeftButtonGroup>
          <RightButtonGroup>
            <InfoButton
              onClick={toggleInfoModal}
              sizeToHide={mediaQueries.max.width['400']}
            />
            <ShareButton
              onClick={trackShare}
              sizeToHide={mediaQueries.max.width['299']}
            />
            {imageUrl && (
              <ExternalLink data-test="thumbnail-link" href={podcastUrl}>
                <BigThumbnail
                  alt={title}
                  src={addFit(imageUrl)}
                  supportMobileView={supportMobileView}
                />
              </ExternalLink>
            )}
          </RightButtonGroup>
        </Toolbar>
      </PlayerContainer>
      {ShareModal}
      {InfoModal}
    </div>
  );
}

export default withPlayerJS(PodcastPlayer);
